/**
 * rbacPermissions.js — Helpers de permissão RBAC do front.
 *
 * REGRA FINAL (Checkpoint 1 da auditoria E2E Acqua, jun/2026):
 * "Cada um no seu quadrado" — edição estritamente por função, zero sobreposição.
 *
 *   1. VISIBILIDADE do detalhe do atleta:
 *      Gestor, Treinador, Preparador, AUX, Biomecânico, Nutricionista → veem TUDO.
 *      Atleta → vê SÓ o próprio.
 *
 *   2. EDIÇÃO de treino FÍSICO:
 *      SÓ Preparador Físico. AUX_PREP_FISICA = viewer puro (herdou permissões
 *      do Treinador via script DB 120, mas nenhum endpoint verifica
 *      dbo.role_permissions — o controle real é neste módulo).
 *
 *   3. EDIÇÃO de treino TÉCNICO/TÁTICO:
 *      SÓ Treinador.
 *
 *   4. plan_type 'mixed' foi ELIMINADO (0 planos em produção; causava sobreposição).
 *      Todo plano nasce 'physical' (dono: Preparador) ou 'technical' (dono: Treinador).
 *
 *   5. Gestor, AUX, Atleta: NENHUM write em treino.
 *
 * REGRA DE LOAD: role = null/undefined → todas as funções retornam false.
 * (Default seguro — atleta nunca cai em 403 transitório.)
 */

// ── Conjuntos canônicos (uppercase, batem com dbo.roles.role_key) ──
// Decisão jun/2026: AUX_PREP_FISICA removido de edição (viewer puro).
const PHYSICAL_EDITOR_ROLES = new Set(['PREPARADOR_FISICO']);
const TECHNICAL_EDITOR_ROLES = new Set(['TREINADOR']);

// Tipos de plano válidos. 'mixed' foi eliminado.
const VALID_PLAN_TYPES = new Set(['physical', 'technical']);

// Staff que enxerga lista de atletas / visão "coach" da agenda e treinos.
// NÃO confundir com permissão de edição.
const STAFF_VIEWER_ROLES = new Set([
  'TREINADOR',
  'PREPARADOR_FISICO',
  'AUX_PREP_FISICA',
  'GESTOR',
  'BIOMECANICO',
  'NUTRICIONISTA',
]);

/**
 * Quem pode VER o detalhe completo de um atleta (todas as seções em modo
 * leitura). Gestor, Treinador, Preparador e demais staff → true.
 * Atleta → false (vê só o próprio em /my/performance, /perfil etc.).
 * Role indefinida → false (default seguro).
 */
export function canViewAthleteDetail(role) {
  if (!role) return false;
  return STAFF_VIEWER_ROLES.has(role);
}

/**
 * Quem pode EDITAR treino físico.
 * SÓ Preparador Físico. AUX, Gestor, Treinador → false.
 * Role indefinida / loading → false.
 */
export function canEditTreinoFisico(role) {
  if (!role) return false;
  return PHYSICAL_EDITOR_ROLES.has(role);
}

/**
 * Quem pode EDITAR treino técnico/tático.
 * SÓ Treinador. AUX, Gestor, Preparador → false.
 * Role indefinida / loading → false.
 */
export function canEditTreinoTecnico(role) {
  if (!role) return false;
  return TECHNICAL_EDITOR_ROLES.has(role);
}

/**
 * Conveniência: o request de treinos aceita `viewAs=athlete|coach`.
 * Qualquer staff vê lista ampla ('coach'); atleta e role-em-loading → 'athlete'.
 */
export function viewAsForTrainingRequests(role) {
  return canViewAthleteDetail(role) ? 'coach' : 'athlete';
}

/**
 * Indica se o usuário pode criar ALGUM tipo de treino.
 */
export function canEditAnyTreino(role) {
  return canEditTreinoFisico(role) || canEditTreinoTecnico(role);
}

/**
 * Para botão "Criar Treino" em página de listagem com tab por tipo.
 * planType: 'physical' | 'technical' — 'mixed' foi eliminado, retorna false.
 * null/undefined (tab "todos") → true se pode criar algum tipo.
 */
export function canCreatePlanOfType(role, planType) {
  if (!role) return false;
  if (planType === 'physical') return canEditTreinoFisico(role);
  if (planType === 'technical') return canEditTreinoTecnico(role);
  // 'mixed' eliminado → false. null/undefined (tab "todos") → pode criar algum.
  if (planType == null) return canEditTreinoFisico(role) || canEditTreinoTecnico(role);
  return false;  // 'mixed' ou valor desconhecido
}

// ════════════════════════════════════════════════════════════════════
// DISCIPLINA (UI fina) — separação Natação × Físico × Academia × etc.
// ════════════════════════════════════════════════════════════════════
//
// `plan_type` no DB é a autoridade RBAC (CHECK IN ('technical','physical',
// 'mixed','recovery')). `discipline` é o filtro de UX fina, ainda não
// persistido — pode ser adicionado depois sem breaking change.
//
// Mapeamento canônico discipline → plan_type:
//   swimming                            → technical  (treinador edita)
//   dryland | gym | mobility | recovery → physical   (preparador edita)
//   other                               → physical   (default seguro)
//   all | null                          → null       (sem filtro)

/** Disciplinas reconhecidas pela UI. */
export const DISCIPLINES = ['swimming', 'dryland', 'gym', 'mobility', 'recovery', 'other'];

/** Mapeia uma disciplina (UI) → plan_type (autoridade backend). */
export function mapDisciplineToPlanType(discipline) {
  if (!discipline || discipline === 'all') return null;
  if (discipline === 'swimming') return 'technical';
  if (['dryland', 'gym', 'mobility', 'recovery', 'other'].includes(discipline)) return 'physical';
  return null;
}

/**
 * Pode EDITAR este plano específico (avalia plan_type + discipline opcional).
 *
 *   • Treinador          → só plan_type='technical' (ou discipline='swimming').
 *   • Preparador Físico  → só plan_type='physical' (ou discipline ∈ dryland,
 *                          gym, mobility, recovery, other).
 *   • Atleta, Gestor, AUX, Bio, Nutri → false sempre.
 *
 * Espelha exatamente o guard backend `_can_edit_plan_type` em
 * routes_training_consolidated.py. Frontend só ESCONDE botões — o backend
 * continua sendo a fonte da verdade.
 */
export function canEditTrainingPlan(role, plan) {
  if (!role || !plan) return false;
  const planType = plan.plan_type;
  const discipline = plan.discipline;

  // Disciplina vence se vier explícita (futuro próximo)
  if (discipline === 'swimming') return canEditTreinoTecnico(role);
  if (['dryland', 'gym', 'mobility', 'recovery', 'other'].includes(discipline)) {
    return canEditTreinoFisico(role);
  }

  // Fallback pelo plan_type (estado atual)
  if (planType === 'technical') return canEditTreinoTecnico(role);
  if (planType === 'physical') return canEditTreinoFisico(role);

  // 'mixed', 'recovery' soltos no plan_type, ou desconhecido → leitura.
  return false;
}
